import { useState } from "react";
import { useAuth } from "@/store/AuthStore";

const inputClass =
  "w-full bg-background-100/70 border border-background-300/50 rounded-md px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/60 focus:ring-2 focus:ring-accent-500/25";

interface LoginFormProps {
  onSwitch: () => void;
}

const LoginForm = ({ onSwitch }: LoginFormProps) => {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Completa correo y contraseña para continuar.");
      return;
    }
    setBusy(true);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo iniciar sesión.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
          Correo electrónico
        </label>
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
            <i className="ri-mail-line text-sm" />
          </span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@correo.com"
            className={`${inputClass} pl-9`}
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Contraseña</label>
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
            <i className="ri-lock-2-line text-sm" />
          </span>
          <input
            type={showPw ? "text" : "password"}
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className={`${inputClass} pl-9 pr-10`}
          />
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-foreground-500 hover:text-accent-300 cursor-pointer"
            aria-label="Mostrar u ocultar contraseña"
          >
            <i className={showPw ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
          </button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-primary-200 bg-primary-500/15 border border-primary-400/30 rounded-md px-3 py-2 flex items-center gap-2 animate-fade-up">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy}
        className="w-full py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 disabled:opacity-60 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
      >
        {busy ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-login-box-line" />}
        {busy ? "Entrando..." : "Iniciar sesión"}
      </button>

      <p className="text-center text-xs text-foreground-600">
        ¿Eres un asesor nuevo?{" "}
        <button
          type="button"
          onClick={onSwitch}
          className="font-semibold text-accent-300 hover:text-accent-200 cursor-pointer"
        >
          Crea tu cuenta aquí
        </button>
      </p>
    </form>
  );
};

export default LoginForm;